import React, { FC, useEffect, useState } from "react";
import "./style.scss";
import ReactCrop, { Crop } from "react-image-crop";
import "react-image-crop/src/ReactCrop.scss";
import { useAppSelector } from "@/redux-config/hooks";
import { selectUser } from "@/redux-config/entities/user";
import { UploadApi } from "@/api";
import { CustomModal } from "@/components/custom-modal";
import { useTranslation } from "react-i18next";

export const ImageUpload: FC<{
  contentFile: File;
  getDataFromServer: Function;
  changeImage: Function;
  setDeleted: Function;
  setDisable: Function;
}> = ({
        contentFile, getDataFromServer, setDeleted, changeImage, setDisable
      }) => {
  const [preview, setPreview] = useState<string>("");
  const [uploadLoader, setUploadLoader] = useState(false);
  const [uploadSending, setUploadSending] = useState(false);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [crop, setCrop] = useState<Crop>();
  const [image, setImage] = useState<HTMLImageElement>();
  const [editedFile, setEditedFile] = useState<File>();
  const [editedFileLink, setEditedFileLink] = useState<string>("");
  const user = useAppSelector(selectUser);

  const { t } = useTranslation();

  useEffect(() => {
    createPreviews();
    uploadToApi();
  }, []);

  const createPreviews = () => {
    if (!contentFile) {
      setPreview("");
      return;
    }
    const objectUrl = URL.createObjectURL(contentFile);
    setPreview(objectUrl);
    setShowModal(true);
    return () => URL.revokeObjectURL(objectUrl);
  };

  const uploadToApi = () => {
    if (!uploadSending) {
      setUploadSending(true);
    }
  };

  const handleCompletedCrop = async (crop: Crop) => {
    if (image) {
      const croppedImageUrl = await getCroppedImg(image, crop);
      setEditedFileLink(croppedImageUrl + "");
    }
  };

  function getCroppedImg(image: HTMLImageElement, crop: Crop): Promise<String> {
    const canvas = document.createElement("canvas");
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    canvas.width = crop.width;
    canvas.height = crop.height;
    const ctx = canvas.getContext("2d");
    if (ctx != null) {
      ctx.drawImage(
        image,
        crop.x * scaleX,
        crop.y * scaleY,
        crop.width * scaleX,
        crop.height * scaleY,
        0,
        0,
        crop.width,
        crop.height
      );
    }

    return new Promise((resolve, reject) => {
      canvas.toBlob((blob) => {
          if (!blob) {
            return;
          }
          setEditedFile(new File([blob], "image.png", { type: blob.type }));
          let file = preview;
          window.URL.revokeObjectURL(file);
          file = window.URL.createObjectURL(blob);
          resolve(file);
        }, image.src.includes("png") ? "image/png" : image.src.includes("jpg") ? "image/jpg" : "image/jpeg",
        1);
    });
  }

  const handleUploadFile = (editedFile: File) => {
    let formData = new FormData();
    formData.append("file", editedFile);
    UploadApi.ImageUploader(user, formData)
      .then((data) => {
        setUploadLoader(false);
        setDisable(false);
        getDataFromServer(data.data);
      })
      .catch((err) => {
        setUploadLoader(false);
        setDisable(false);
        getDataFromServer(undefined);
      });
  };

  return (
    <div
      className=" mx-2 position-relative overflow-hidden"
      style={{ width: "150px", height: "150px" }}
    >
      {uploadLoader && (
        <div
          className="w-100 h-100 position-absolute text-white contents-upload-image d-flex justify-content-center align-items-center"
          style={{
            zIndex: 3
          }}
        >
          <small>Uploading</small>
        </div>
      )}

      {preview ? (
        <img
          className="rounded-4 mx-2"
          width={240}
          height={140}
          src={preview + ""}
          alt={"image uploaded"}
        />
      ) : (
        <></>
      )}

      <CustomModal
        size={'lg'}
        setShowModal={()=>{
          setShowModal(false)
          setDeleted(contentFile);
        }}
        showModal={showModal}
      >
        <div className={"d-flex flex-column"}>
          <div className={'d-flex flex-row justify-content-center'}>
            <div className={"border-reg position-relative"}>
              <ReactCrop
                crop={crop}
                onChange={(c) => setCrop(c)}
                onComplete={(c) => {
                  handleCompletedCrop(c);
                }}
              >
                <img
                  style={{maxHeight:'80dvh'}}
                  src={preview}
                  onLoad={(e: React.SyntheticEvent<HTMLImageElement, Event>) => {
                    setImage(e.currentTarget);
                  }}
                />
              </ReactCrop>
            </div>
          </div>
          <div className={"d-flex flex-row mt-2"}>
            <button
              className={"prm-btn rounded-2 px-3 py-2"}
              onClick={() => {
                changeImage(contentFile, editedFile);
                setPreview(editedFileLink);
                if (editedFile) {
                  handleUploadFile(editedFile);
                } else {
                  handleUploadFile(contentFile);
                }
                setShowModal(false);
              }}
            >
              {t("submit")}
            </button>
          </div>
        </div>
      </CustomModal>
    </div>
  );
};